import React, { useState, useLayoutEffect, useEffect, useRef, useCallback } from "react";
import WizardWrapper from "../../components/wizard/WizardWrapper";

import * as AssociateInformation from "../../forms/WorkersCompensation/AssociateInformation";
import * as IncidentTimeAndPlace from "../../forms/WorkersCompensation/IncidentTimeAndPlace";

import * as IncidentInformation from "../../forms/WorkersCompensation/IncidentInformation";
import * as PhysicianInformation from "../../forms/WorkersCompensation/PhysicianInformation";
import * as FileAttachments from "../../forms/WorkersCompensation/FileAttachments";

import FormContent from "../../forms/FormContent";
import { FormFieldConfigType, FormStateType, FormValidationType } from "../../forms/types";
import { resetUpdatedForm, saveForm, setValueInFormsState } from "../../forms/formsSlice";

import ClaimSubmitted from "../../views/ClaimSubmitted";

import { Form, TextContent, Box, WizardProps } from "@amzn/awsui-components-console";

import ResetFormModal from "../../components/reset-form-modal/ResetFormModal";
import moment from "moment-timezone";

import { MetricsPublisher } from "../../metrics";

import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { createWcClaim, requestAppReset, setValueInAppState } from "../appSlice";
import {
  FORM_PAGE_ERROR_MESSAGE,
  MAX_STEPS,
  STEP_NAMES,
  DUPLICATE_MODAL_TITLE,
} from "../constants";
import { useAppSelector } from "src/redux/hooks";

import ReviewPage from "../../forms/reviewPage/ReviewPage";
import { getSite } from "src/sites/sitesSlice";
import {
  ANCHOR,
  ASSOCIATE_INFORMATION_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
  INCIDENT_INFORMATION_CONSTANTS,
  INCIDENT_TIME_AND_PLACE_CONSTANTS,
  PHYSICIAN_INFORMATION_CONSTANTS,
  REVIEW_PAGE_CONSTANTS,
  UNSUPPORTED_TPA,
  SEDGWICK_CANADA,
} from "src/forms/constants";

import type { ResetFormModalContents, SimLinkClaimModel } from "../types";
import { cloneDeep } from "lodash";
import { TPA_FORM_OVERRIDES } from "src/forms/WorkersCompensation/tpaOverrides";
import { getFieldConfig } from "src/forms/tools";
import { getReferralReasonsByProvince } from "src/forms/WorkersCompensation/sedgwickCanadaReferralReasons";
import { SiteTypes } from "src/sites/constants";
import { isDevEnvironment } from "src/environmentInfo/EnvironmentInfo";
import WorkersCompensationAlert from "./WorkersCompensationAlert";
import {
  getWcCountryConfig,
  hasCountryAccess,
  isNonUsCountryConfig,
} from "src/app/wcCountryConfig";
import { useFeatureFlags } from "src/app/featureFlags";
import { resolveWcTpa } from "src/jsonForms/preScreen/wcTpaResolution";
import { convertFormToSymLinkClaimModel } from "src/utils/convertFormToSymlink";
import { CreateWcClaimCommandInput } from "@amzn/ttechclaimintakeservice-client";
import { buildWcCreateClaimInput } from "src/api/claims/WCClaimModelBuilder";

const DEFAULT_FORM_CONFIGS = {
  [ASSOCIATE_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(AssociateInformation.formConfig),
  [INCIDENT_TIME_AND_PLACE_CONSTANTS.FORM_NAME]: cloneDeep(IncidentTimeAndPlace.formConfig),
  [INCIDENT_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(IncidentInformation.formConfig),
  [PHYSICIAN_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(PhysicianInformation.formConfig),
  [FILE_ATTACHMENTS_CONSTANTS.FORM_NAME]: cloneDeep(FileAttachments.formConfig),
};
const DEFAULT_FORM_VALIDATIONS = {
  [ASSOCIATE_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(AssociateInformation.formValidation),
  [INCIDENT_TIME_AND_PLACE_CONSTANTS.FORM_NAME]: cloneDeep(IncidentTimeAndPlace.formValidation),
  [INCIDENT_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(IncidentInformation.formValidation),
  [PHYSICIAN_INFORMATION_CONSTANTS.FORM_NAME]: cloneDeep(PhysicianInformation.formValidation),
  [FILE_ATTACHMENTS_CONSTANTS.FORM_NAME]: cloneDeep(FileAttachments.formValidation),
};

function WorkersCompensationApp() {
  const dispatch = useDispatch<AppDispatch>();

  //Redux Forms state
  const {
    errors,
    allFormStates,
    validating,
    updatedForm,
    employeeDetailsRetrieved,
    workersCompensationTpa,
    tpaChangeDetected,
    previousWorkersCompensationTpa,
  } = useAppSelector((state: RootState) => state.forms);
  //Redux App state
  const {
    duplicateClaim,
    duplicateClaimChecked,
    triggerOpenResetFormModal,
    remoteResetFormModalContents,
    amazonClaimReferenceId,
  } = useAppSelector((state: RootState) => state.app);

  //Redux Auth state
  const { user } = useAppSelector((state: RootState) => state.auth);

  //Redux Wizard state
  const { goingBack } = useAppSelector((state: RootState) => state.wizard);
  const wizardState = useAppSelector((state: RootState) => state.wizard);
  //Redux Sites state
  const { incidentSite, associateHomeSite } = useAppSelector((state: RootState) => state.sites);
  // Subscribe to feature flag loading so the TPA resolver effect re-runs
  // once flags have arrived. The actual flag values are read via the
  // `FeatureFlags` proxy inside `getWcCountryConfig`'s `isEnabled`.
  const { isLoading: featureFlagsLoading } = useFeatureFlags();
  //Local state variables
  const [renderSubmitScreen, setRenderSubmitScreen] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [formattedFormErrors, setFormattedFormErrors] = useState(null);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [resetFormModalOpen, setResetFormModalOpen] = useState(false);
  const [resetFormModalContents, setResetFormModalContents] = useState<ResetFormModalContents>({
    title: "",
    description: null,
    confirm: "",
    cancel: "",
  });
  const [claimModel, setClaimModel] = useState<SimLinkClaimModel | undefined>(undefined);

  const [localFormConfigs, setLocalFormConfigs] = useState(DEFAULT_FORM_CONFIGS);
  const [localFormValidations, setLocalFormValidations] = useState(DEFAULT_FORM_VALIDATIONS);

  const [maintenanceTimeWindowActive, setMaintenanceTimeWindowActive] = useState(false);
  const [loadingCountryCheck, setLoadingCountryCheck] = useState(false);
  const incidentDate = allFormStates[INCIDENT_TIME_AND_PLACE_CONSTANTS.FORM_NAME].incidentDate;
  function createFinalForm(): {
    createClaimInput: CreateWcClaimCommandInput;
    symLinkClaimModel: SimLinkClaimModel;
  } {
    let formState: FormStateType = {};
    let formConfig: FormFieldConfigType[] = [];
    for (const formName in localFormConfigs) {
      formState = { ...formState, ...allFormStates[formName] };
      formConfig = [...formConfig, ...localFormConfigs[formName as keyof typeof localFormConfigs]];
    }
    // We need to add these fields to the model builder to avoid getting
    // Redux store values outside of React components
    const additionalFields = {
      incidentSite,
      associateHomeSite,
      user,
      workersCompensationTpa,
      amazonClaimReferenceId,
    };

    const symLinkClaimModel = convertFormToSymLinkClaimModel(formState, {
      incidentSite,
      associateHomeSite,
      amazonClaimReferenceId,
    });
    const createClaimInput = buildWcCreateClaimInput(formState, formConfig, additionalFields);
    return { createClaimInput, symLinkClaimModel };
  }
  /**
   * useEffect that runs every time component mounts, used to set initial values.
   */
  useEffect(() => {
    setFormattedFormErrors(null);
  }, []);

  /**
   * useLayoutEffect that runs when the form is being validated and to check if we can submit the form
   * and display errors
   */
  useLayoutEffect(() => {
    if (validating) {
      return;
    }
    if (!errors.length && submitClicked) {
      submitForm();
      return;
    }
    setSubmitClicked(false);
    if (errors.length) {
      const errorText: any = (
        <div role="alert" title="form-errors" tabIndex={-1}>
          <TextContent>
            <span className="form-page-error">{FORM_PAGE_ERROR_MESSAGE}</span>
          </TextContent>
        </div>
      );
      if (!goingBack) {
        dispatch(resetUpdatedForm());
        setFormattedFormErrors(errorText);
      }
    }
  }, [errors, validating, submitClicked]);

  /**
   * useEffect that checks if the error banner is present and focuses into it for accessibility purposes.
   */
  useEffect(() => {
    if (formattedFormErrors) {
      const errorElement = document.querySelector('[title="form-errors"]');
      if (errorElement) {
        (errorElement as HTMLElement).tabIndex = 0;
        (errorElement as HTMLElement).focus();
        window.scrollTo(0, document.body.scrollHeight);
      }
    }
  }, [formattedFormErrors]);
  /**
   * useEffect that runs whenever a duplicateClaim has been found
   */
  useEffect(() => {
    if (duplicateClaim && duplicateClaimChecked) {
      const {
        claimNumber,
        incidentTimestamp,
        primaryInjuredBodyPartDescription,
        secondaryInjuredBodyPartDescription,
      } = duplicateClaim;
      const incidentTime = moment.unix(incidentTimestamp).format("MMMM Do YYYY, h:mm:ss a");
      const description = (
        <TextContent>
          <Box textAlign="left">
            <div className="modal-text">
              <div>
                A claim has already been submitted for this employee with the following information:
                <ul>
                  <li>Claim Number: {claimNumber}</li>
                  <li>Date and Time: {incidentTime}</li>
                  <li>
                    Injured Body Parts:
                    <ul>
                      {primaryInjuredBodyPartDescription ? (
                        <li>{primaryInjuredBodyPartDescription}</li>
                      ) : null}
                      {secondaryInjuredBodyPartDescription ? (
                        <li>{secondaryInjuredBodyPartDescription}</li>
                      ) : null}
                    </ul>
                  </li>
                </ul>
              </div>
              <div>
                <b>Please verify this is not a duplicate submission.</b>
              </div>
              <br />
              <div>Would you like to continue to file a new claim for the same date?</div>
            </div>
          </Box>
        </TextContent>
      );
      const metricsPublisher = new MetricsPublisher("duplicateClaimAttempt");
      metricsPublisher.publishDuplicateClaimAttemptMetric(user.id);

      setResetFormModalContents({
        title: DUPLICATE_MODAL_TITLE,
        description,
        cancel: "Yes",
        confirm: "No, Reset Form",
      });
      setResetFormModalOpen(true);
    } else {
      setResetFormModalOpen(false);
    }
  }, [duplicateClaim, duplicateClaimChecked]);

  /**
   * useEffect that checks if form has been updated to remove the error banner.
   */
  useEffect(() => {
    if (updatedForm && formattedFormErrors) {
      setFormattedFormErrors(null);
    }
  }, [updatedForm]);

  /**
   * useEffect that triggers the reset modal to open
   */
  useEffect(() => {
    if (triggerOpenResetFormModal) {
      setResetFormModalContents(remoteResetFormModalContents);
      setResetFormModalOpen(true);
      dispatch(setValueInAppState({ key: "triggerOpenResetFormModal", value: false }));
    }
  }, [triggerOpenResetFormModal]);

  /**
   * Resolves the Third Party Administrator (TPA) based on the current
   * incident site and date. The resolution rules live in `resolveWcTpa`
   * so the same logic is shared with the JSON-forms WC pre-screen.
   */
  const getTpaFromSiteAndDate = () =>
    resolveWcTpa({
      country: incidentSite?.country,
      state: incidentSite?.state,
      incidentDate: incidentDate.value,
    });
  /**
   * useLayoutEffect hook to set TPA when incidentDate or incidentSite values change before render
   */
  useLayoutEffect(() => {
    const countryConfig = incidentSite?.country
      ? getWcCountryConfig(incidentSite.country)
      : undefined;
    const hasNonUsConfig = countryConfig && isNonUsCountryConfig(countryConfig);
    if (!incidentSite?.state && !hasNonUsConfig) {
      return;
    }
    const detectedTpa = getTpaFromSiteAndDate();
    if (!detectedTpa) {
      return;
    }
    // Only trigger TPA change detection if the TPA actually changed
    // This prevents duplicate site API calls from resetting form state overrides
    if (detectedTpa !== workersCompensationTpa) {
      dispatch(
        setValueInFormsState({
          key: "workersCompensationTpa",
          value: detectedTpa,
        })
      );
      dispatch(setValueInFormsState({ key: "tpaChangeDetected", value: true }));
    }
  }, [incidentSite, incidentDate.value, featureFlagsLoading]);

  /**
   * useEffect hook to set site information when retrieving user's information
   */
  useEffect(() => {
    if (employeeDetailsRetrieved) {
      const associateHomeSiteCode =
        allFormStates[ASSOCIATE_INFORMATION_CONSTANTS.FORM_NAME]?.[
          ASSOCIATE_INFORMATION_CONSTANTS.ASSOCIATE_HOME_SITE_CODE
        ];
      if (associateHomeSiteCode && !incidentSite) {
        dispatch(
          getSite({
            code: associateHomeSiteCode.value,
            type: SiteTypes.ALL_CLAIM_SITES,
          })
        );
      }
    }
  }, [employeeDetailsRetrieved]);
  /**
   * Change formConfig and formValidation if TPA is updated
   */

  const applyConfigOverrides = (formConfig: FormFieldConfigType[]) => {
    const formOverride = TPA_FORM_OVERRIDES[workersCompensationTpa];
    for (const fieldName in formOverride.config) {
      const fieldToUpdate = getFieldConfig(fieldName, formConfig);
      if (fieldToUpdate) {
        Object.assign(fieldToUpdate, {
          ...fieldToUpdate,
          ...formOverride.config[fieldName],
        });
      }
    }
    return formConfig;
  };

  const applyValidationOverrides = (formValidation: FormValidationType) => {
    const formOverride = TPA_FORM_OVERRIDES[workersCompensationTpa];
    for (const fieldName in formOverride.validation) {
      const fieldToUpdate = formValidation[fieldName as keyof typeof formValidation];
      if (fieldToUpdate) {
        Object.assign(fieldToUpdate, {
          ...fieldToUpdate,
          ...formOverride.validation[fieldName],
        });
      }
    }
    return formValidation;
  };

  const applyStateOverrides = (formState: FormStateType) => {
    const formOverride = TPA_FORM_OVERRIDES[workersCompensationTpa];
    for (const fieldName in formOverride.state) {
      const fieldToUpdate = formState[fieldName as keyof typeof formState];
      if (fieldToUpdate) {
        Object.assign(fieldToUpdate, {
          ...fieldToUpdate,
          ...formOverride.state[fieldName],
        });
      }
    }
    return formState;
  };

  /**
   * Helper function to apply TPA overrides to form configs and validations
   */
  const applyTpaOverrides = useCallback(() => {
    const tempLocalFormConfigs = cloneDeep(DEFAULT_FORM_CONFIGS);
    const tempLocalFormValidations = cloneDeep(DEFAULT_FORM_VALIDATIONS);

    Object.values(tempLocalFormConfigs).forEach((config) => {
      applyConfigOverrides(config);
    });
    Object.values(tempLocalFormValidations).forEach((formValidation) => {
      applyValidationOverrides(formValidation);
    });

    setLocalFormConfigs(tempLocalFormConfigs);
    setLocalFormValidations(tempLocalFormValidations);
  }, [workersCompensationTpa]);

  /**
   * useLayoutEffect hook to apply form overrides on initial load if TPA is already set
   */
  useLayoutEffect(() => {
    if (workersCompensationTpa && workersCompensationTpa !== UNSUPPORTED_TPA) {
      applyTpaOverrides();
    }
  }, []); // Empty dependency array - only run on mount

  /**
   * useLayoutEffect hook to apply form overrides before render if TPA was changed
   */
  useLayoutEffect(() => {
    if (!tpaChangeDetected) return;
    dispatch(setValueInFormsState({ key: "tpaChangeDetected", value: false }));
    // Form cannot be submitted if the TPA is not supported
    if (workersCompensationTpa === UNSUPPORTED_TPA) {
      return;
    }

    const tempAllFormStates = cloneDeep(allFormStates);
    applyTpaOverrides();

    /**
     * We only update the form state if the previous TPA is different from the new one
     * this ensures we can keep user changes when reloading the page like hiding or showing
     * different fields. We do need to update config and validation values since those are
     * not persistent on reload.
     */
    if (previousWorkersCompensationTpa !== workersCompensationTpa) {
      for (const formName in tempAllFormStates) {
        if (formName === ASSOCIATE_INFORMATION_CONSTANTS.FORM_NAME) {
          continue;
        }
        const currentFormState = tempAllFormStates[formName as keyof typeof tempAllFormStates];
        applyStateOverrides(currentFormState);
        dispatch(saveForm({ formName, formState: currentFormState }));
      }
    }
    dispatch(
      setValueInFormsState({
        key: "previousWorkersCompensationTpa",
        value: workersCompensationTpa,
      })
    );
  }, [workersCompensationTpa, tpaChangeDetected, previousWorkersCompensationTpa]);

  const previousProvinceRef = useRef<string | null>(null);

  /**
   * useEffect that dynamically populates referral reasons options based on the
   * incident site province when TPA is "sedgwickCanada".
   */
  useEffect(() => {
    if (workersCompensationTpa !== SEDGWICK_CANADA || !incidentSite?.state) {
      return;
    }
    const province = incidentSite.state;
    const referralOptions = getReferralReasonsByProvince(province);

    // Update the referral reasons field options in the local form config
    setLocalFormConfigs((prevConfigs) => {
      const updatedConfigs = cloneDeep(prevConfigs);
      const incidentInfoConfig = updatedConfigs[INCIDENT_INFORMATION_CONSTANTS.FORM_NAME];
      const referralField = getFieldConfig(
        INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS,
        incidentInfoConfig
      );
      if (referralField) {
        (referralField as any).options = referralOptions;
      }
      return updatedConfigs;
    });

    // Only clear referral reasons if province actually changed
    if (previousProvinceRef.current && previousProvinceRef.current !== province) {
      const currentFormState = allFormStates[INCIDENT_INFORMATION_CONSTANTS.FORM_NAME];
      const currentReferralState =
        currentFormState?.[INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS];
      if (currentReferralState?.value?.length > 0) {
        const currentOtherDescriptionState =
          currentFormState?.[INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS_OTHER_DESCRIPTION];
        dispatch(
          saveForm({
            formName: INCIDENT_INFORMATION_CONSTANTS.FORM_NAME,
            formState: {
              [INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS]: {
                ...currentReferralState,
                value: [],
                errors: [],
              },
              [INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS_OTHER_DESCRIPTION]: {
                ...currentOtherDescriptionState,
                value: "",
                errors: [],
                hidden: true,
                validationOverride: { required: { value: false } },
              },
            },
          })
        );
      }
    }
    previousProvinceRef.current = province;
  }, [incidentSite?.state, workersCompensationTpa]);

  /**
   * Checks if the current time in Central Time is within the maintenance window (12:00 AM - 1:00 AM Central Time)
   * and updates the 'maintenanceTimeWindowActive' value accordingly.
   */
  function checkMaintenanceWindow() {
    const centralDateTime = moment().utc().tz("America/Chicago");
    // Check if the current hour is 0 (12:00 AM)
    if (centralDateTime.hour() === 0) {
      if (!maintenanceTimeWindowActive) {
        setMaintenanceTimeWindowActive(true);
      }
    } else {
      if (maintenanceTimeWindowActive) {
        setMaintenanceTimeWindowActive(false);
      }
    }
  }
  /**
   * Set an interval for every minute to check when the TPA maintenance is active
   */
  useEffect(() => {
    checkMaintenanceWindow();
    const interval = setInterval(checkMaintenanceWindow, 60000);
    return () => {
      // Clear interval when component dismounts
      clearInterval(interval);
    };
  }, [checkMaintenanceWindow]);

  /**
   * Check if maintenance window is active and current TPA is Anchor, if so, set
   * tpaMaintenance to true which will be read by the WorkersCompensationAlert component
   * to display the maintenance alert.
   */
  useEffect(() => {
    if (maintenanceTimeWindowActive && workersCompensationTpa === ANCHOR) {
      dispatch(setValueInAppState({ key: "tpaMaintenance", value: true }));
    }
    if (!maintenanceTimeWindowActive) {
      dispatch(setValueInAppState({ key: "tpaMaintenance", value: false }));
    }
  }, [maintenanceTimeWindowActive, workersCompensationTpa]);

  /**
   * Checks whether the user's roles match the incident site's country.
   * Returns true if the user is authorized to submit a claim for the
   * incident site country. Uses the centralized WC country config.
   */
  function checkCountryRoleMatch(): boolean {
    return hasCountryAccess(user.roles, incidentSite?.country);
  }

  /**
   * Opens the access control modal with messaging from the WC country config
   * for the incident site's country.
   */
  function openAccessControlModal() {
    const countryConfig = getWcCountryConfig(incidentSite?.country);
    const description = (
      <TextContent>
        <Box textAlign="left">
          <div className="modal-text">
            <div>{countryConfig.accessDeniedModal.description}</div>
            <div>
              Please select a different incident site that matches your role, or reset the form to
              start over.
            </div>
          </div>
        </Box>
      </TextContent>
    );
    setResetFormModalContents({
      title: countryConfig.accessDeniedModal.title,
      description,
      cancel: "Dismiss",
      confirm: "Reset Form",
    });
    setResetFormModalOpen(true);
  }

  /**
   * Callback fired by WizardWrapper when the user clicks Next.
   * On step 2 (Incident Time and Place), validates that the user's role matches
   * the incident site country before allowing progression.
   */
  function onNextStep() {
    const stepIndex = wizardState.currentIndex;
    const incidentTimeAndPlaceStepIndex = STEP_NAMES.indexOf(
      INCIDENT_TIME_AND_PLACE_CONSTANTS.FORM_NAME
    );
    if (stepIndex !== incidentTimeAndPlaceStepIndex) {
      return;
    }
    const allowed = checkCountryRoleMatch();
    if (!allowed) {
      setLoadingCountryCheck(true);
      openAccessControlModal();
    }
  }

  function submitForm() {
    const { createClaimInput, symLinkClaimModel } = createFinalForm();

    dispatch(setValueInAppState({ key: "submitScreenStatus", value: "loading" }));
    dispatch(createWcClaim({ createWcClaimInput: createClaimInput }));

    setFormattedFormErrors(null);
    setSubmitClicked(false);
    setRenderSubmitScreen(true);
    setClaimModel(symLinkClaimModel);
  }
  function onSubmit() {
    setSubmitClicked(true);
  }
  function onBackToEdit() {
    setCurrentStep(MAX_STEPS - 1);
    setRenderSubmitScreen(false);
  }
  function onResetForm() {
    const description = (
      <TextContent>
        <Box textAlign="left">
          <div className="modal-text">
            <div>Are you sure you want to reset the form?</div>
            <div>All progress will be lost and all of the form information will be deleted.</div>
          </div>
        </Box>
      </TextContent>
    );
    setResetFormModalContents({
      title: "Reset Form",
      description,
      cancel: "Cancel",
      confirm: "Reset Form",
    });

    setResetFormModalOpen(true);
  }
  function onStartNewClaim() {
    dispatch(requestAppReset());
  }
  function onPrevious() {
    if (formattedFormErrors) setFormattedFormErrors(null);
  }
  function onSkipTo(goingBack: boolean) {
    if (goingBack) {
      if (formattedFormErrors) setFormattedFormErrors(null);
    }
  }

  if (renderSubmitScreen) {
    return (
      <ClaimSubmitted
        key="claim submitted"
        onBackToEdit={onBackToEdit}
        onStartNewClaim={onStartNewClaim}
        claimModel={claimModel}
      />
    );
  }
  const steps: WizardProps.Step[] = [
    {
      title: AssociateInformation.formHeader,
      description: AssociateInformation.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={FORM_PAGE_ERROR_MESSAGE}>
          <FormContent
            formName={AssociateInformation.formName}
            formConfig={localFormConfigs[AssociateInformation.formName]}
            formValidation={localFormValidations[AssociateInformation.formName]}
            data-testid="associate-information-form"
            key="associate-information-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },

    {
      title: IncidentTimeAndPlace.formHeader,
      description: IncidentTimeAndPlace.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={FORM_PAGE_ERROR_MESSAGE}>
          <FormContent
            formName={IncidentTimeAndPlace.formName}
            formConfig={localFormConfigs[IncidentTimeAndPlace.formName]}
            formValidation={localFormValidations[IncidentTimeAndPlace.formName]}
            data-testid="incident-time-and-place-form"
            key="incident-time-and-place-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: IncidentInformation.formHeader,
      description: IncidentInformation.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={FORM_PAGE_ERROR_MESSAGE}>
          <FormContent
            formName={IncidentInformation.formName}
            formConfig={localFormConfigs[IncidentInformation.formName]}
            formValidation={localFormValidations[IncidentInformation.formName]}
            data-testid="incident-information-form"
            key="incident-information-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: PhysicianInformation.formHeader,
      description: PhysicianInformation.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={FORM_PAGE_ERROR_MESSAGE}>
          <FormContent
            formName={PhysicianInformation.formName}
            formConfig={localFormConfigs[PhysicianInformation.formName]}
            formValidation={localFormValidations[PhysicianInformation.formName]}
            data-testid="physician-information-form"
            key="physician-information-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: FileAttachments.formHeader,
      description: FileAttachments.formDescription,
      content: (
        <Form errorText={formattedFormErrors} errorIconAriaLabel={FORM_PAGE_ERROR_MESSAGE}>
          <FormContent
            formName={FileAttachments.formName}
            formConfig={localFormConfigs[FileAttachments.formName]}
            formValidation={localFormValidations[FileAttachments.formName]}
            data-testid="file-attachments-form"
            key="file-attachments-form"
          />
        </Form>
      ),
      isOptional: isDevEnvironment(),
    },
    {
      title: REVIEW_PAGE_CONSTANTS.FORM_NAME,
      description: REVIEW_PAGE_CONSTANTS.FORM_DESCRIPTION,
      content: <ReviewPage allFormConfigs={localFormConfigs}></ReviewPage>,
      isOptional: isDevEnvironment(),
    },
  ];
  return (
    <React.Fragment>
      <ResetFormModal
        setModalOpen={setResetFormModalOpen}
        onResetForm={() => {
          setLoadingCountryCheck(false);
          onStartNewClaim();
        }}
        onCancel={() => {
          setLoadingCountryCheck(false);
          setResetFormModalOpen(false);
        }}
        isModalOpen={resetFormModalOpen}
        modalTitle={resetFormModalContents.title}
        modalDescription={resetFormModalContents.description}
        cancelButtonText={resetFormModalContents.cancel}
        confirmButtonText={resetFormModalContents.confirm}
      />
      <WorkersCompensationAlert />
      <WizardWrapper
        onSubmit={onSubmit}
        onNext={onNextStep}
        onSkipTo={onSkipTo}
        onPrevious={onPrevious}
        onReset={onResetForm}
        currentStep={currentStep}
        stepNames={STEP_NAMES}
        allFormValidations={localFormValidations}
        steps={steps}
        loadingNextStep={loadingCountryCheck}
      />
    </React.Fragment>
  );
}
export default WorkersCompensationApp;
